import { useCallback, useEffect, useMemo, useRef } from 'react';

import { reportQuality, type QualityKind } from '../model/quality';
import { createQualityTracker } from '../model/qualityTracker';

/**
 * Владеет накопителем телеметрии одного медиа-соединения и отправляет сводку, когда сеанс
 * закончился.
 *
 * Две причины, по которым это отдельный хук, а не пара строк в `useCallRoom`/`useVoiceRoom`:
 *
 * 1. Отправка нужна не только на выходе, но и на выгрузке страницы. Вкладку закрывают прямо
 *    посреди созвона, и именно эти сеансы — оборвавшиеся — интереснее всего. Обычный fetch
 *    браузер отменяет вместе с документом, поэтому там keepalive.
 * 2. Накопитель обнуляется при снятии, а цель (комната) может смениться. Держать это в общем
 *    месте дешевле, чем повторять в двух хуках и однажды разойтись.
 */
export const useQualityReporter = (kind: QualityKind) => {
  const trackerRef = useRef(createQualityTracker());
  /**
   * Куда отчитываться: звонок для 1:1, сбор для войса (комната у сбора одна). Ref, а не state —
   * значение читают обработчики событий, и лишний рендер на каждую смену комнаты не нужен.
   */
  const targetRef = useRef<string | null>(null);

  /**
   * Назначить цель и начать отсчёт. Сброс здесь обязателен: хук живёт, пока открыта страница, а
   * сеанс начинается в момент подключения — без сброса в длительность попало бы всё время до
   * входа в комнату, и доля плохой связи размазалась бы по нему.
   */
  const setTarget = useCallback((refId: string | null) => {
    targetRef.current = refId;
    trackerRef.current.reset();
  }, []);

  /**
   * Отправить накопленное и закрыть сеанс. Цель обнуляется: слушатель `pagehide` живёт, пока
   * открыта страница, и без обнуления вкладка, закрытая через час после двухминутного звонка,
   * прислала бы вторую сводку на час — сервер сложил бы её с первой, и звонок превратился бы в
   * «час молчания».
   */
  const flush = useCallback(
    (keepalive = false) => {
      const refId = targetRef.current;
      const tracker = trackerRef.current;
      // Слишком короткий сеанс не несёт информации, а без refId отчитываться просто некуда.
      if (!refId || !tracker.hasData()) {
        // Цель всё равно снимаем: сеанс закончился, и второй раз о нём говорить не о чем.
        targetRef.current = null;
        return;
      }
      void reportQuality(kind, refId, tracker.take(), keepalive);
      targetRef.current = null;
    },
    [kind]
  );

  useEffect(() => {
    const onPageHide = () => flush(true);
    // pagehide, а не beforeunload: последний не срабатывает на мобильных при сворачивании, а
    // именно там вкладку и «закрывают» — переключением на игру.
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [flush]);

  // Стабильная ссылка: объект попадает в зависимости эффектов, которые владеют соединением.
  // Новый объект на каждый рендер пересоздавал бы комнату — то есть рвал бы разговор.
  return useMemo(
    () => ({ tracker: trackerRef.current, setTarget, flush }),
    [setTarget, flush]
  );
};
